import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { Float, Text } from "@react-three/drei";
import { ArenaCanvas } from "../three/Arena";
import TeamStage from "../three/TeamStage";
import { useGame, useGameEvent } from "../game/GameProvider";
import { errorToastStyle, infoToastStyle, successToastStyle } from "../utils/utils";
import { FONTS, ARENA } from "../game/theme";
import { sfx } from "../game/sounds";

function Roster({ side, title, players, userId }) {
  return (
    <div className={`roster ${side}`}>
      <h4>{title}</h4>
      <ul>
        {players.length === 0 && <li className="empty">recruiting…</li>}
        {players.map((p) => (
          <li key={p.id}>
            {p.name}
            {p.is_admin && <span className="mini-badge admin">HOST</span>}
            {p.id === userId && <span className="mini-badge you">YOU</span>}
          </li>
        ))}
      </ul>
    </div>
  );
}

const Lobby = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const roomId = searchParams.get("roomId") || "";

  const { room, userId, team, questionsLoaded, sendMessage } = useGame();
  const [subject, setSubject] = useState("");
  const [countdown, setCountdown] = useState(null);
  const [loadingQuestions, setLoadingQuestions] = useState(false);
  const prevPlayerCount = useRef(0);
  const countdownRef = useRef(null);
  const statusRetries = useRef(0);

  const players = useMemo(() => room?.player_details || [], [room]);
  const me = players.find((p) => p.id === userId);
  const isAdmin = !!me?.is_admin;
  const myTeam = me?.team || team || (isAdmin ? "left" : "");
  const leftTeam = players.filter((p) => p.team === "left");
  const rightTeam = players.filter((p) => p.team === "right");

  const goToGame = React.useCallback(() => {
    navigate(
      `/game?roomId=${encodeURIComponent(roomId)}&userId=${encodeURIComponent(
        userId
      )}&team=${myTeam}`
    );
  }, [navigate, roomId, userId, myTeam]);
  const goToGameRef = useRef(goToGame);
  goToGameRef.current = goToGame;

  // Ask for the roster once the server has acked us (userId set). On a fresh
  // socket (page refresh) there is no ack coming — ask after a grace period so
  // we don't race the createRoom/joinRoom Lambda that's still writing the room.
  useEffect(() => {
    if (!roomId) return;
    if (userId) {
      sendMessage({ action: "roomStatus", roomId });
      return;
    }
    const t = setTimeout(
      () => sendMessage({ action: "roomStatus", roomId }),
      1500
    );
    return () => clearTimeout(t);
  }, [userId, roomId, sendMessage]);

  // New player pop sound
  useEffect(() => {
    if (players.length > prevPlayerCount.current && prevPlayerCount.current > 0) {
      sfx.pop();
    }
    prevPlayerCount.current = players.length;
  }, [players.length]);

  useGameEvent("error", (err) => {
    // "Room not found" right after creating is usually the roomStatus request
    // racing the createRoom write — retry quietly before giving up.
    if (err === "Room not found" && !room && statusRetries.current < 3) {
      statusRetries.current += 1;
      setTimeout(
        () => sendMessage({ action: "roomStatus", roomId }),
        800 * statusRetries.current
      );
      return;
    }
    toast.error(err, errorToastStyle);
    setLoadingQuestions(false);
    if (err === "Room already exists" || err === "Room not found") {
      setTimeout(() => navigate("/"), 1500);
    }
  });

  useGameEvent("message", (msg) => {
    if (msg.includes("Joined room")) {
      sendMessage({ action: "roomStatus", roomId });
    }
    if (msg === "Game started" || msg === "Questions Loaded") return;
    toast.info(msg, infoToastStyle);
  });

  useGameEvent("questionsLoaded", () => {
    setLoadingQuestions(false);
    toast.success("Questions ready!", successToastStyle);
    sfx.pop();
  });

  const startCountdown = React.useCallback(() => {
    if (countdownRef.current) return;
    sfx.roundIntro();
    setCountdown(5);
    countdownRef.current = setInterval(() => {
      setCountdown((prev) => (prev === null ? prev : Math.max(0, prev - 1)));
    }, 1000);
  }, []);

  // Side effects of the countdown live here, not in the state updater.
  useEffect(() => {
    if (countdown === null) return;
    if (countdown === 0) {
      clearInterval(countdownRef.current);
      countdownRef.current = null;
      goToGameRef.current();
    } else {
      sfx.tick();
    }
  }, [countdown]);

  useGameEvent("gameStarted", startCountdown);

  // If the room is already live (e.g. we refreshed), jump straight in.
  useEffect(() => {
    if (room?.roomStatus === "active" && countdown === null) {
      goToGameRef.current();
    }
  }, [room?.roomStatus, countdown]);

  useEffect(() => () => clearInterval(countdownRef.current), []);

  const handlePopulate = () => {
    setLoadingQuestions(true);
    sendMessage({ action: "populateQuestions", roomId, subject });
  };

  const handleStart = () => {
    sendMessage({ action: "startGame", roomId });
    startCountdown();
  };

  const copyRoomId = () => {
    navigator.clipboard.writeText(roomId);
    toast.success("Room code copied!", successToastStyle);
  };

  return (
    <>
      <ArenaCanvas cameraBase={[0, 4.6, 11]} cameraLook={[0, 1, 1]} sway={1.2}>
        <TeamStage teamKey="left" players={leftTeam} showLabel={false} />
        <TeamStage teamKey="right" players={rightTeam} showLabel={false} />
        <Float speed={2} rotationIntensity={0.35} floatIntensity={0.9}>
          <Text
            font={FONTS.display}
            position={[0, 3.2, -1]}
            fontSize={1.4}
            color={ARENA.gold}
            outlineWidth={0.07}
            outlineColor="#3d2c00"
            anchorX="center"
          >
            VS
          </Text>
        </Float>
      </ArenaCanvas>

      <div className="hud-top">
        <div className="chip">
          ROOM <span className="gold">{roomId}</span>
          <button
            className="icon-btn"
            style={{ padding: "2px 8px", fontSize: 13, marginLeft: 4 }}
            onClick={copyRoomId}
            title="Copy room code"
          >
            📋
          </button>
        </div>
        <div className="chip">
          <span className="lime">{leftTeam.length}</span> VS{" "}
          <span className="violet">{rightTeam.length}</span> · SHARE THE CODE!
        </div>
      </div>

      <Roster side="left" title="GREEN TITANS" players={leftTeam} userId={userId} />
      <Roster side="right" title="VIOLET VIPERS" players={rightTeam} userId={userId} />

      {countdown !== null && countdown > 0 && (
        <div className="announce">
          <p className="big">{countdown}</p>
          <p className="sub">SHOWTIME!</p>
        </div>
      )}

      {isAdmin && countdown === null && (
        <div className="lobby-console">
          <div className="c-title">🎙️ HOST CONSOLE</div>
          <input
            className="ainput"
            type="text"
            placeholder="TOPIC — BOLLYWOOD, OFFICE LIFE, CRICKET, 90s KIDS…"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
          />
          <div className="row">
            <button
              className="abtn abtn-violet"
              onClick={handlePopulate}
              disabled={loadingQuestions}
            >
              {loadingQuestions ? "SUMMONING…" : "LOAD QUESTIONS"}
            </button>
            <button
              className="abtn abtn-lime"
              onClick={handleStart}
              disabled={!questionsLoaded}
            >
              START THE SHOW
            </button>
          </div>
        </div>
      )}

      {!isAdmin && countdown === null && (
        <div className="waiting-pill">
          <div className="chip">
            WAITING FOR THE HOST TO START<span className="dots" />
          </div>
        </div>
      )}

      <ToastContainer />
    </>
  );
};

export default Lobby;
